import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { FieldGroup } from 'anf-core-react';
import { useController } from 'react-hook-form';
import * as FormStructure from '../FormStructure';
import { TmntText } from '../Common';
import { useCheckboxGroup } from '../../hooks';
import { StepTwoPropTypes, StepTwoDefaultProps } from './props';
import {
  fields as stepTwoFields,
  MarketingBrands,
  LegalSection,
} from './StepTwo/index';
import { JOIN_NOW_FORM_FLAGS_QUERY } from './operations';

export default function StepTwo({
  clearErrors,
  control,
  getValues,
  onMarketingBrandsChange,
  queryData,
  setError,
}) {
  const [isTooltipOpen, setIsToolTipOpen] = useState(false);

  const { data: flagData } = useQuery(
    JOIN_NOW_FORM_FLAGS_QUERY,
    { ssr: false, context: { batch: true } },
  );

  const { textFor, config } = queryData;
  const { label: legendLabel, formGroupList } = textFor.joinForm;
  const [, personalInformationGroup = [], marketingBrandsGroup = []] = formGroupList;
  const { check: joinLegalCheck, text: joinLegalText } = textFor.joinLegal;
  const { joinFormPrivacyTerms } = textFor;

  // Marketing brands state management
  const [
    marketingBrands,,
    updateMarketingBrands,
    isMarketingBrandChecked,
  ] = useCheckboxGroup(config?.autoSelectedMarketingBrands);

  // Form controllers
  const { field: legalField, fieldState: legalFieldState } = useController({
    name: 'legal-check',
    control,
    defaultValue: false,
    rules: { required: joinLegalCheck },
  });

  const { field: marketingBrandsField } = useController({
    name: 'marketing-brands[]',
    control,
    defaultValue: marketingBrands,
  });

  const { field: genderField } = useController({
    name: 'gender',
    control,
    defaultValue: null,
  });

  // Event handlers
  const handleMarketingBrandsChange = (event) => {
    const { value } = event.target;
    const brands = updateMarketingBrands(event.target);
    marketingBrandsField.onChange(brands);
    if (onMarketingBrandsChange) onMarketingBrandsChange(value);
  };

  const fields = personalInformationGroup.fieldList?.reduce((acc, field) => {
    if (field.name === 'first-name') acc.firstName = field;
    else if (field.name === 'last-name') acc.lastName = field;
    else if (field.name === 'phone') acc.phone = field;
    return acc;
  }, {}) || {};

  return (
    <>
      <FormStructure.FormCell align="center">
        <div className="h3">
          <TmntText tmnt={textFor?.almostDone} />
        </div>
      </FormStructure.FormCell>
      <FormStructure.FormCell>
        <FieldGroup legend={legendLabel?.value}>
          { fields.firstName && stepTwoFields['first-name'](fields.firstName, control) }
          { fields.lastName && stepTwoFields['last-name'](fields.lastName, control) }
        </FieldGroup>
      </FormStructure.FormCell>
      {
        fields.phone && (
          <FormStructure.FormCell>
            {
              stepTwoFields.phone(
                fields.phone,
                control,
                {
                  textFor, config, isTooltipOpen, setIsToolTipOpen,
                },
              )
            }
          </FormStructure.FormCell>
        )
      }
      {
        !(flagData?.hasJoinFlowPreferencesEnabled)
          ? null
          : (
            <>
              <FormStructure.FormCell>
                {
                  stepTwoFields.birthday(
                    control,
                    {
                      textFor, clearErrors, setError, getValues,
                    },
                  )
                }
              </FormStructure.FormCell>
              <FormStructure.FormCell>
                { stepTwoFields.preferences({ textFor, genderField }) }
              </FormStructure.FormCell>
            </>
          )
      }
      <FormStructure.FormCell>
        <MarketingBrands
          marketingBrandsGroup={marketingBrandsGroup}
          isMarketingBrandChecked={isMarketingBrandChecked}
          handleMarketingBrandsChange={handleMarketingBrandsChange}
          marketingBrandsField={marketingBrandsField}
        />
      </FormStructure.FormCell>
      <FormStructure.FormCell>
        <hr />
      </FormStructure.FormCell>
      <LegalSection
        joinFormPrivacyTerms={joinFormPrivacyTerms}
        joinLegalCheck={joinLegalCheck}
        joinLegalText={joinLegalText}
        legalField={legalField}
        legalFieldState={legalFieldState}
      />
    </>
  );
}

StepTwo.propTypes = StepTwoPropTypes;
StepTwo.defaultProps = StepTwoDefaultProps;
